import React, { useState } from "react";
import { useParams } from "react-router";
import { getLevelPrefix } from "@sscs/report/utils";
import { QueryStatus } from "@sscs/types";
import {
  Button,
  Card,
  Checkbox,
  Collapse,
  Link,
  Skeleton,
  Switch,
  Tooltip,
} from "@sscs/ui";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CloseIcon,
  LockIcon,
} from "@sscs/ui/icons";
import { getClassName } from "@sscs/utils";
import { PERMISSION } from "Constants";
import { useAllowedTo } from "Hooks";
import { useConfigurationProvider, useDerivedScanLevel } from "../../hooks";
import { useGetPolicyMappingsQuery } from "../../services/policies.api";
import styles from "./configureLevel.module.scss";

const NO_EDIT_RIGHTS = "You do not have permission to change levels";

const LEVELS = [1, 2, 3, 4, 5];

const LEVEL_NAMES: Record<number, string> = {
  1: "Minimum requirements",
  2: "Recommended baseline",
  3: "Established standard",
  4: "Hardened integrity",
  5: "Maximum assurance",
};

type ConfigureLevelProps = {
  scope: string;
  status: QueryStatus;
};

const ConfigureLevel = ({
  scope,
  status,
}: ConfigureLevelProps): React.JSX.Element => {
  const {
    canDisableLevels,
    derivedScanLevel,
    handleLevelChange,
    customizedPolicies,
    currentAutoApprovalEnabled,
    updateAutoApproval,
  } = useDerivedScanLevel();
  const { autoApprovalEnabled } = useConfigurationProvider();
  const { data: policyMappings, isLoading } = useGetPolicyMappingsQuery();
  const { groupId } = useParams();
  const canEditConfiguration = useAllowedTo({
    permissions: groupId
      ? [PERMISSION.CONFIGURE_GROUP_POLICY_PROFILE]
      : [PERMISSION.CONFIGURE_ORGANIZATION_POLICY_PROFILE],
    groupId: groupId,
  });

  const [openLevels, setOpenLevels] = useState<Set<number>>(() => new Set());

  const fetching = status.status === "FETCHING" || isLoading;
  const customized = customizedPolicies.length > 0;
  const prefix = getLevelPrefix(customized);
  const selectedLevel = derivedScanLevel ?? 0;

  const groupsFor = (level: number) =>
    policyMappings?.policy_mappings[`L${level}`] ?? [];
  const countFor = (level: number) => groupsFor(level).length;

  const toggleLevel = (level: number) => {
    setOpenLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  };

  const allOpen = openLevels.size === LEVELS.length;
  const toggleAll = (): void =>
    setOpenLevels(allOpen ? new Set() : new Set(LEVELS));

  const cellVariant = (rowLevel: number, column: number) => {
    if (column < rowLevel) return "empty";
    if (column <= selectedLevel) return "filled";
    return "faint";
  };

  const renderCells = (rowLevel: number) =>
    LEVELS.map((column) => {
      const variant = cellVariant(rowLevel, column);
      return (
        <div
          key={column}
          className={getClassName(styles.cell, {
            [styles["col-selected"]]:
              column === selectedLevel && rowLevel <= selectedLevel,
          })}
        >
          {variant === "filled" && (
            <CheckIcon
              size="xs"
              color="interactive"
              className={styles["mark-check"]}
            />
          )}
          {variant === "faint" && (
            <CloseIcon
              size="xs"
              color="disabled"
              className={styles["mark-cross"]}
            />
          )}
        </div>
      );
    });

  const renderCheckAreaCells = (rowLevel: number) =>
    LEVELS.map((column) => (
      <div
        key={column}
        className={getClassName(styles.cell, {
          [styles["col-selected"]]:
            column === selectedLevel && rowLevel <= selectedLevel,
        })}
      />
    ));

  const levelsOffReason =
    selectedLevel === 0 ? 'Turn on "Use SAFE levels" to select a level' : null;
  const levelDisabledReason = canEditConfiguration
    ? levelsOffReason
    : NO_EDIT_RIGHTS;

  const isAutoApprovalDisabled =
    derivedScanLevel === 0 || fetching || !canEditConfiguration;

  return (
    <Card.Card>
      <Card.Title
        details={customized ? ["* Levels have been customized"] : undefined}
        actions={
          <div className={styles.toggle}>
            {canDisableLevels ? (
              <Tooltip
                disabled={canEditConfiguration}
                title={NO_EDIT_RIGHTS}
                trigger="control"
              >
                <span className={styles["toggle-row"]}>
                  <Switch
                    disabled={!canEditConfiguration}
                    label="Use SAFE levels"
                    checked={selectedLevel !== 0}
                    onChange={(_event, checked) =>
                      handleLevelChange(checked ? null : 0)
                    }
                  />
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="SAFE levels are enforced by the organization and cannot be disabled at group level.">
                <span className={styles["toggle-row"]}>
                  <Switch label="Use SAFE levels" checked disabled />
                  <LockIcon />
                </span>
              </Tooltip>
            )}
          </div>
        }
      >
        Configure SAFE levels for {scope}
      </Card.Title>
      <Card.Content flush>
        {fetching ? (
          <div className={styles.intro}>
            <Skeleton variant="rectangular" width="100%" height={320} />
          </div>
        ) : (
          <div
            className={getClassName(styles.matrix, {
              [styles["matrix-off"]]: selectedLevel === 0,
            })}
          >
            <div className={styles["header-row"]}>
              <div className={styles["head-label"]}>
                <span className={styles["head-text"]}>
                  Select the SAFE level to enforce. Each level adds checks on
                  top of the levels below it.
                </span>
                <Button
                  variant="text"
                  size="small"
                  aria-expanded={allOpen}
                  onClick={toggleAll}
                >
                  {allOpen ? "Collapse all" : "Expand all"}
                </Button>
              </div>
              {LEVELS.map((column) => {
                const reached = selectedLevel > 0 && column <= selectedLevel;
                const selected = selectedLevel > 0 && column === selectedLevel;
                return (
                  <div
                    key={column}
                    className={getClassName(styles["diamond-cell"], {
                      [styles["col-selected"]]: selected,
                    })}
                  >
                    <Tooltip
                      disabled={levelDisabledReason === null}
                      title={levelDisabledReason}
                    >
                      <span className={styles["diamond-trigger"]}>
                        <button
                          type="button"
                          className={getClassName(styles.diamond, {
                            [styles["diamond-reached"]]: reached,
                            [styles["diamond-unreached"]]: !reached,
                            [styles["diamond-selected"]]: selected,
                          })}
                          aria-label={`Enforce up to ${prefix}${column}`}
                          aria-pressed={selected}
                          disabled={levelDisabledReason !== null}
                          onClick={() => handleLevelChange(column)}
                        >
                          <span className={styles["diamond-label"]}>
                            {getLevelPrefix(customizedPolicies.length > 0)}
                            {column}
                          </span>
                        </button>
                      </span>
                    </Tooltip>
                  </div>
                );
              })}
            </div>

            {LEVELS.map((level) => {
              const checks = groupsFor(level).map((levelInfo) => ({
                id: levelInfo.id,
                text: `No ${levelInfo.label}`,
                customized: levelInfo.policies.some((policyId) =>
                  customizedPolicies.includes(policyId),
                ),
              }));
              const isOpen = openLevels.has(level);
              return (
                <div key={level} className={styles.group}>
                  <div className={styles["group-row"]}>
                    <button
                      type="button"
                      className={styles["group-label-area"]}
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? "Collapse" : "Expand"} ${prefix}${level} ${LEVEL_NAMES[level]}`}
                      onClick={() => toggleLevel(level)}
                    >
                      {isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                      <strong className={styles["group-label"]}>
                        {LEVEL_NAMES[level]}
                      </strong>
                      <span className={styles["group-count"]}>
                        {`+${countFor(level)} checks`}
                      </span>
                    </button>
                    {renderCells(level)}
                  </div>
                  <Collapse transitionIn={isOpen}>
                    <div className={styles["check-area"]}>
                      <ul className={styles["check-list"]}>
                        {checks.map((check) => (
                          <li key={check.id} className={styles["check-item"]}>
                            {check.text}
                            {check.customized && (
                              <span className={styles.asterisk}> *</span>
                            )}
                          </li>
                        ))}
                      </ul>
                      {renderCheckAreaCells(level)}
                    </div>
                  </Collapse>
                </div>
              );
            })}
          </div>
        )}
        {scope === "group" && (
          <Card.Actions>
            <div
              className={getClassName(styles["configure-auto-approval"], {
                [styles["configure-auto-approval-disabled"]]:
                  isAutoApprovalDisabled,
              })}
            >
              <Tooltip
                disabled={canEditConfiguration}
                title="You do not have permission to enable auto approval for this group"
              >
                <span className={styles["enable-checkbox-trigger"]}>
                  <Checkbox
                    className={styles["enable-checkbox"]}
                    disableRipple={true}
                    checked={
                      !fetching
                        ? (currentAutoApprovalEnabled ?? autoApprovalEnabled)
                        : false
                    }
                    onChange={(event) =>
                      updateAutoApproval(event.target.checked)
                    }
                    disabled={isAutoApprovalDisabled}
                  />
                  <span>
                    Enable <strong>automatic approval</strong> of packages based
                    on selected SAFE Level.
                  </span>
                </span>
              </Tooltip>
              <Link
                href="https://docs.secure.software/portal/portal-policy-config#auto-approval"
                target="_blank"
                rel="noreferrer"
                inline
              >
                Find out more
              </Link>
            </div>
          </Card.Actions>
        )}
      </Card.Content>
    </Card.Card>
  );
};

export default ConfigureLevel;
